import { AbschnittDisplayModel, AenderungsantragDisplayModel, AnfrageDisplayModel, GlobalEnumerationsAceFormulartyp, GlobalEnumerationsAceTypaenderungsantrag, LocalizedTextDisplayModel, MitantragstellerDisplayModel } from "@/api/model-index";
import { AenderungsantragService, MitantragstellerService } from "@/api/service-index";
import { inject, Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { I18nService } from "./i18nService";
import { PortalContextService } from "./portal-context.service";

@Injectable({
    providedIn: 'root'
})
export class AnfrageTransformerService {
    protected readonly languageService: I18nService;

    constructor(languageService: I18nService) {
        this.languageService = languageService;
    }

    public async transform(anfrage: AnfrageDisplayModel): Promise<AnfrageDisplayModel> {
        if (!anfrage)
            return anfrage;

        anfrage = Object.assign({}, anfrage);

        if (!anfrage.phase?.abschnitte)
            return anfrage;

        for (const abschnitt of anfrage.phase.abschnitte) {
            if (abschnitt.formularTyp == GlobalEnumerationsAceFormulartyp.Mitantragsteller) {
                anfrage = await this.transformMitantragsteller(abschnitt, anfrage);
            }
            if (abschnitt.formularTyp == GlobalEnumerationsAceFormulartyp.Aenderungsantrag) {
                anfrage = await this.transformAnderungsantraege(abschnitt, anfrage);
            }
        }

        return anfrage;
    }

    protected mitantragstellerService = inject(MitantragstellerService);
    protected async transformMitantragsteller(abschnitt: AbschnittDisplayModel, anfrage: AnfrageDisplayModel): Promise<AnfrageDisplayModel> {
        abschnitt.abschnitte = [];

        if (!anfrage.mitantragsteller) {
            anfrage.mitantragsteller = await this.mitantragstellerService.apiMitantragstellerGetAllMitantragstellerAnfrageIdGet({ anfrageId: anfrage.id }).toPromise();
        }

        anfrage.mitantragsteller.sort((a, b) => {
            const firstnameOrder = (a.firstname ?? "").localeCompare(b.firstname ?? "");

            if (firstnameOrder == 0)
                return (a.lastname ?? "").localeCompare(b.lastname ?? "");
            return firstnameOrder;
        }).forEach(x => {
            abschnitt.abschnitte!.push(this.createAbschnittForMitantragsteller(x, abschnitt.id!));
        });

        const foundIndex = anfrage.phase!.abschnitte!.findIndex(x => x.id === abschnitt.id);
        if (foundIndex !== -1) {
            anfrage.phase!.abschnitte![foundIndex] = abschnitt;
        }

        return anfrage;
    }

    protected createAbschnittForMitantragsteller(mitantragsteller: MitantragstellerDisplayModel, parentAbschnittId: string): AbschnittDisplayModel {
        let abschnittName = "";

        if (mitantragsteller.firstname || mitantragsteller.lastname) {
            abschnittName = ((mitantragsteller.firstname ?? "") + " " + (mitantragsteller.lastname ?? "")).trim();
        }
        else {
            abschnittName = mitantragsteller.emailAddress || this.languageService.instant("mitantragsteller.abschnitt.noname");
        }

        const newAbschnitt: AbschnittDisplayModel = new AbschnittDisplayModel();
        newAbschnitt.id = mitantragsteller.id!;
        newAbschnitt.name = abschnittName;
        newAbschnitt.kurzTitel = environment.languages.enabled.map(x => ({ localeCode: x, text: abschnittName } as LocalizedTextDisplayModel));
        newAbschnitt.infotext = [];
        newAbschnitt.formularTyp = GlobalEnumerationsAceFormulartyp.MitantragstellerDaten;
        newAbschnitt.abschnitte = [];
        newAbschnitt.fragen = [];
        newAbschnitt.parentId = parentAbschnittId;
        newAbschnitt.beantragteFoerdersumme = null;
        newAbschnitt.reihenfolge = 0;

        return newAbschnitt;
    }

    protected aenderungsantragService = inject(AenderungsantragService);
    protected portalContextService = inject(PortalContextService);
    protected async transformAnderungsantraege(abschnitt: AbschnittDisplayModel, anfrage: AnfrageDisplayModel): Promise<AnfrageDisplayModel> {
        let aenderungsantraege: AenderungsantragDisplayModel[] = [];

        if (this.portalContextService.Aenderungsantraege) {
            aenderungsantraege = this.portalContextService.Aenderungsantraege;
        }
        else {
            aenderungsantraege = await this.aenderungsantragService.apiAenderungsantragGetAllProjektIdGet({ projektId: anfrage.id }).toPromise();
            this.portalContextService.Aenderungsantraege = aenderungsantraege;
        }

        abschnitt.abschnitte = [];

        aenderungsantraege
            .sort((a, b) => b.createdOn!.localeCompare(a.createdOn!))
            .forEach(x => {
                abschnitt.abschnitte!.push(this.createAbschnittForAenderungsantrag(x, abschnitt.id!));
            });

        const foundIndex = anfrage.phase!.abschnitte!.findIndex(x => x.id === abschnitt.id);
        if (foundIndex !== -1) {
            anfrage.phase!.abschnitte![foundIndex] = abschnitt;
        }

        return anfrage;
    }

    protected createAbschnittForAenderungsantrag(aenderungsantrag: AenderungsantragDisplayModel, parentAbschnittId: string): AbschnittDisplayModel {
        let abschnittName = this.languageService.getEnumValue(GlobalEnumerationsAceTypaenderungsantrag, aenderungsantrag.typ)?.Description;

        const newAbschnitt: AbschnittDisplayModel = new AbschnittDisplayModel();
        newAbschnitt.id = aenderungsantrag.id!;
        newAbschnitt.name = abschnittName;
        newAbschnitt.kurzTitel = environment.languages.enabled.map(x => ({ localeCode: x, text: abschnittName } as LocalizedTextDisplayModel));
        newAbschnitt.infotext = [];
        newAbschnitt.typAenderungsantrag = aenderungsantrag.typ;
        newAbschnitt.abschnitte = [];
        newAbschnitt.fragen = [];
        newAbschnitt.parentId = parentAbschnittId;
        newAbschnitt.beantragteFoerdersumme = null;
        newAbschnitt.reihenfolge = 0;

        return newAbschnitt;
    }
}